import { useEffect, useRef, useState } from 'react';
import { ModalShell } from '@busy/ui';
import { useT } from '../lib/i18n';

// iOS-style scrollable time picker. Two columns (minutes 0-59, seconds 0-55
// in 5s steps), each is a vertical scroll-snap list. Tap a value to center
// it, or scroll/drag and the snap engine locks to the nearest item.
//
// Used by the cut-timer editor for phase durations — much friendlier on
// touch than the previous MMSS text input which popped the keyboard and
// fought every gesture.

type Props = {
  open: boolean;
  /** Total duration in seconds. */
  totalSec: number;
  onConfirm: (sec: number) => void;
  onClose: () => void;
  /** Optional override for what's being timed — populates the modal title. */
  title?: string;
};

const ITEM_HEIGHT = 44;
const VISIBLE_ROWS = 5; // odd → middle row is the selection
const PAD_ROWS = Math.floor(VISIBLE_ROWS / 2);

export function TimePickerWheel({ open, totalSec, onConfirm, onClose, title }: Props) {
  const t = useT();
  const initialMin = Math.floor(totalSec / 60);
  const initialSec = totalSec % 60;
  const [minutes, setMinutes] = useState(initialMin);
  const [seconds, setSeconds] = useState(roundToFive(initialSec));

  // Reset when the picker opens with a fresh source value.
  useEffect(() => {
    if (open) {
      setMinutes(initialMin);
      setSeconds(roundToFive(initialSec));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const minuteValues = Array.from({ length: 60 }, (_, i) => i);
  const secondValues = Array.from({ length: 12 }, (_, i) => i * 5); // 0, 5, …, 55

  return (
    <ModalShell open onClose={onClose} size="sm" ariaLabel={t('timePicker.aria')}>
      <div className="flex flex-col bg-surface rounded-2xl">
        <div className="shrink-0 px-5 pt-5 pb-2 text-center">
          <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-gold-500 mb-1">
            {t('timePicker.eyebrow')}
          </div>
          <h2 className="font-display text-xl">{title ?? t('timePicker.title')}</h2>
        </div>

        <div className="flex items-center justify-center gap-6 px-5 py-4 relative">
          {/* Selection indicator — sits behind the columns, centered on
              the middle row. The columns scroll over it so the active
              value lands inside the indicator. */}
          <div
            aria-hidden
            className="absolute left-5 right-5 top-1/2 -translate-y-1/2 h-[44px] rounded-xl bg-gold-500/12 dark:bg-gold-500/15 border-y border-gold-500/40 pointer-events-none"
          />
          <WheelColumn
            label={t('timePicker.minutes')}
            values={minuteValues}
            selected={minutes}
            onSelect={setMinutes}
          />
          <span className="font-display text-2xl text-ink-500">:</span>
          <WheelColumn
            label={t('timePicker.seconds')}
            values={secondValues}
            selected={seconds}
            onSelect={setSeconds}
          />
        </div>

        <div className="shrink-0 border-t border-line p-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-11 rounded-lg border border-line bg-bg-alt font-semibold text-ink-700"
          >
            {t('timePicker.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(minutes * 60 + seconds)}
            className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold"
          >
            {t('timePicker.set')}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function WheelColumn({
  label,
  values,
  selected,
  onSelect,
}: {
  label: string;
  values: number[];
  selected: number;
  onSelect: (v: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const settleTimer = useRef<number | null>(null);

  // Initial scroll-to-selected on mount.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const idx = values.indexOf(selected);
    if (idx >= 0) {
      el.scrollTop = idx * ITEM_HEIGHT;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read the selected value off scroll position once the user lets go +
  // the scroll snap settles. Throttled via timeout so we only re-render
  // after the scroll has stopped — no thrashing.
  function handleScroll() {
    const el = ref.current;
    if (!el) return;
    if (settleTimer.current) window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => {
      const idx = Math.round(el.scrollTop / ITEM_HEIGHT);
      const next = values[idx];
      if (typeof next === 'number' && next !== selected) onSelect(next);
    }, 90);
  }

  function handleTap(v: number) {
    const el = ref.current;
    if (!el) return;
    const idx = values.indexOf(v);
    if (idx >= 0) el.scrollTo({ top: idx * ITEM_HEIGHT, behavior: 'smooth' });
    onSelect(v);
  }

  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500">
        {label}
      </span>
      <div
        ref={ref}
        onScroll={handleScroll}
        className="relative w-20 overflow-y-scroll snap-y snap-mandatory hide-scrollbar"
        style={{
          height: ITEM_HEIGHT * VISIBLE_ROWS,
          // Padding above/below so first + last values can center under
          // the selection indicator.
          paddingTop: ITEM_HEIGHT * PAD_ROWS,
          paddingBottom: ITEM_HEIGHT * PAD_ROWS,
        }}
      >
        {values.map((v) => {
          const isSelected = v === selected;
          return (
            <button
              key={v}
              type="button"
              onClick={() => handleTap(v)}
              className={`block w-full snap-center text-center font-display tabular-nums transition-colors ${
                isSelected
                  ? 'text-navy-900 dark:text-gold-400 text-2xl'
                  : 'text-ink-500 text-xl'
              }`}
              style={{ height: ITEM_HEIGHT, lineHeight: `${ITEM_HEIGHT}px` }}
            >
              {String(v).padStart(2, '0')}
            </button>
          );
        })}
      </div>
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { scrollbar-width: none; }
      `}</style>
    </div>
  );
}

function roundToFive(s: number): number {
  return Math.round(s / 5) * 5;
}
